import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCriminalWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { AccusedRecord } from '../../../types/criminalCaseRequest';
import type { LookupItem } from '../../../mocks/criminalMasterData';
import FormattedDateInput from '../../../components/ui/FormattedDateInput';
import FormattedDateRangeInput from '../../../components/ui/FormattedDateRangeInput';
import { exceedsMax, isFutureIsoDate, isTodayOrFutureIsoDate, maxCharLimitExceededError, todayIsoDateLocal, sliceMax } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, validateRequired, type RequiredErrors, type RequiredFieldSpec } from '../../../utils/requiredValidation';
import {
  requiredFieldError,
  validationDateFutureOnly,
  validationDateOfOffenceFuture,
} from '../../../utils/validationI18n';
import { SectionCard } from '../WizardComponents';
import { CriminalWizardActions } from './CriminalWizardActions';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { criminalWizardPath, CriminalWizardShell, FieldError, inpClass } from './CriminalWizardShell';
import { isOtherLookup } from './criminalConstants';
import { useCriminalLookups } from './useCriminalLookups';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_TEXT = 2000;

function newAccused(): AccusedRecord {
  return { id: crypto.randomUUID(), name: '', status: '', statusOther: '' };
}

function isStatusOther(statusId: string, statuses: LookupItem[]): boolean {
  const row = statuses.find((s) => s.id === statusId);
  return row ? isOtherLookup(row.name, row.id) : statusId === 'other';
}

function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

export default function CriminalStep3OffenceAccusedPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useCriminalWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const { urgencyLevels, suspectStatuses } = useCriminalLookups('step3');
  const maxOffenceDate = useMemo(() => todayIsoDateLocal(), []);
  const minDeadlineDate = useMemo(() => todayIsoDateLocal(), []);

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const specs: RequiredFieldSpec[] = [
      { key: 'offenceDateFrom', label: f.dateOfOffence, value: draft.offenceDateFrom, elementId: 'crim-offenceFrom' },
      { key: 'urgencyLevelId', label: f.urgencyLevel, value: draft.urgencyLevelId, elementId: 'crim-urgency' },
    ];

    if (draft.accusedDetailsAvailable) {
      draft.accused.forEach((a, i) => {
        specs.push(
          { key: `accused-name-${i}`, label: f.accusedName, value: a.name, elementId: `crim-accused-name-${i}` },
          { key: `accused-status-${i}`, label: f.accusedSuspects, value: a.status, elementId: `crim-accused-status-${i}` },
        );
      });
    }

    const reqErrs = validateRequired(specs);
    const extra: RequiredErrors = {};

    if (draft.offenceDateFrom && isFutureIsoDate(draft.offenceDateFrom)) {
      extra.offenceDateFrom = validationDateOfOffenceFuture();
    } else if (draft.offenceDateTo && isFutureIsoDate(draft.offenceDateTo)) {
      extra.offenceDateFrom = validationDateOfOffenceFuture();
    }

    if (exceedsMax(draft.placeOfOffence, MAX_TEXT)) extra.placeOfOffence = maxCharLimitExceededError();
    if (exceedsMax(draft.urgencyJustification, MAX_TEXT)) extra.urgencyJustification = maxCharLimitExceededError();

    if (draft.accusedDetailsAvailable) {
      draft.accused.forEach((a, i) => {
        if (a.name.trim() && exceedsMax(a.name, MAX_TEXT)) extra[`accused-name-${i}`] = maxCharLimitExceededError();
        if (isStatusOther(a.status, suspectStatuses) && !a.statusOther.trim()) {
          extra[`accused-statusOther-${i}`] = requiredFieldError('Status (Other)');
        }
      });
    }

    if (draft.urgencyLevelId && !draft.urgencyJustification.trim()) {
      extra.urgencyJustification = requiredFieldError('Urgency Justification');
    }

    if (draft.deadline && !isTodayOrFutureIsoDate(draft.deadline)) {
      extra.deadline = validationDateFutureOnly();
    }

    const all = { ...reqErrs, ...extra };
    setErrors(all);
    if (Object.keys(all).length) {
      const allSpecs: RequiredFieldSpec[] = [
        ...specs,
        { key: 'placeOfOffence', label: f.placeOfOffence, value: draft.placeOfOffence, elementId: 'crim-placeOfOffence' },
        { key: 'urgencyJustification', label: 'Urgency Justification', value: draft.urgencyJustification, elementId: 'crim-urgencyJustification' },
        { key: 'deadline', label: 'Deadline', value: draft.deadline, elementId: 'crim-deadline' },
        ...(draft.accusedDetailsAvailable
          ? draft.accused.flatMap((a, i) =>
              isStatusOther(a.status, suspectStatuses)
                ? [{ key: `accused-statusOther-${i}`, label: 'Status (Other)', value: a.statusOther, elementId: `crim-accused-statusOther-${i}` }]
                : [],
            )
          : []),
      ];
      focusFirstInvalid(allSpecs, all);
      return false;
    }
    return true;
  };

  const updateAccused = (id: string, patch: Partial<AccusedRecord>) => {
    setDraft((d) => ({ ...d, accused: d.accused.map((a) => (a.id === id ? { ...a, ...patch } : a)) }));
  };

  const addAccused = () => setDraft((d) => ({ ...d, accused: [...d.accused, newAccused()] }));
  const removeAccused = (id: string) =>
    setDraft((d) => ({ ...d, accused: d.accused.length > 1 ? d.accused.filter((a) => a.id !== id) : d.accused }));

  return (
    <CriminalWizardShell currentStep={3} draft={draft}>
      <SectionCard title={f.sectionOffenceDetails}>
        <div className="ccr-fg ccr-field-half" style={{ marginBottom: 14 }}>
          <label>
            {f.dateOfOffence} <span className="req">*</span>
          </label>
          <FormattedDateRangeInput
            id="crim-offenceFrom"
            className={inpClass(Boolean(errors.offenceDateFrom))}
            from={draft.offenceDateFrom}
            to={draft.offenceDateTo}
            maxDate={maxOffenceDate}
            enableYmdViews
            onChange={(from, to) => {
              setDraft((d) => ({ ...d, offenceDateFrom: from, offenceDateTo: to }));
              clearErr('offenceDateFrom');
            }}
          />
          <FieldError message={errors.offenceDateFrom} />
        </div>

        <div className="ccr-fg" style={{ marginBottom: 0 }}>
          <label>{f.placeOfOffence}</label>
          <textarea
            id="crim-placeOfOffence"
            className={inpClass(Boolean(errors.placeOfOffence))}
            rows={3}
            value={draft.placeOfOffence}
            onChange={(e) => setDraft((d) => ({ ...d, placeOfOffence: maxLen('placeOfOffence', e.target.value, MAX_TEXT) }))}
            placeholder={f.enterPlaceOfOffence}
          />
          <FieldError message={errors.placeOfOffence} />
        </div>
      </SectionCard>

      <div className="ccr-accused-panel">
        <div className="ccr-accused-panel-hdr">Accused / Suspects</div>
        <div className="ccr-accused-panel-body">
          <div className="ccr-fg" style={{ marginBottom: draft.accusedDetailsAvailable ? 12 : 0 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                id="crim-accused-available"
                type="checkbox"
                checked={draft.accusedDetailsAvailable}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setDraft((d) => ({
                    ...d,
                    accusedDetailsAvailable: checked,
                    accused: checked ? (d.accused.length ? d.accused : [newAccused()]) : [],
                  }));
                  if (!checked) {
                    setErrors((prev) => {
                      const next = { ...prev };
                      for (const key of Object.keys(next)) {
                        if (key.startsWith('accused-')) delete next[key];
                      }
                      return next;
                    });
                  }
                }}
              />
              <span>
                {f.accusedDetailsAvailable} <span className="req">*</span>
              </span>
            </label>
          </div>

          {draft.accusedDetailsAvailable && (
            <>
              {draft.accused.map((a, i) => (
                <div key={a.id} style={{ marginBottom: i < draft.accused.length - 1 ? 12 : 0 }}>
                  <div className="ccr-accused-row">
                    <div className="ccr-fg">
                      <label>
                        {f.accusedName} <span className="req">*</span>
                      </label>
                      <input
                        id={`crim-accused-name-${i}`}
                        className={inpClass(Boolean(errors[`accused-name-${i}`]))}
                        type="text"
                        maxLength={MAX_TEXT}
                        value={a.name}
                        onChange={(e) => {
                          updateAccused(a.id, { name: sliceMax(e.target.value, MAX_TEXT) });
                          clearErr(`accused-name-${i}`);
                        }}
                        placeholder={f.enterAccusedName}
                      />
                      <FieldError message={errors[`accused-name-${i}`]} />
                    </div>
                    <div className="ccr-fg">
                      <label>
                        {f.accusedSuspects} <span className="req">*</span>
                      </label>
                      <WizardSelect
                        id={`crim-accused-status-${i}`}
                        hasError={Boolean(errors[`accused-status-${i}`])}
                        value={a.status}
                        placeholder={f.selectStatus}
                        options={suspectStatuses.map((s) => ({ id: s.id, label: s.name }))}
                        onChange={(id) => {
                          updateAccused(a.id, { status: id, statusOther: '' });
                          clearErr(`accused-status-${i}`);
                          clearErr(`accused-statusOther-${i}`);
                        }}
                      />
                      <FieldError message={errors[`accused-status-${i}`]} />
                    </div>
                    <button
                      type="button"
                      className="ccr-accused-del"
                      aria-label={`Remove accused ${i + 1}`}
                      disabled={draft.accused.length <= 1}
                      onClick={() => removeAccused(a.id)}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                  {isStatusOther(a.status, suspectStatuses) ? (
                    <div className="ccr-accused-other ccr-fg">
                      <label>
                        Status (Other) <span className="req">*</span>
                      </label>
                      <input
                        id={`crim-accused-statusOther-${i}`}
                        className={inpClass(Boolean(errors[`accused-statusOther-${i}`]))}
                        type="text"
                        maxLength={255}
                        placeholder="Specify status"
                        value={a.statusOther}
                        onChange={(e) => updateAccused(a.id, { statusOther: sliceMax(e.target.value, 255) })}
                      />
                      <FieldError message={errors[`accused-statusOther-${i}`]} />
                    </div>
                  ) : null}
                </div>
              ))}

              <button className="ccr-btn-ol" type="button" style={{ marginTop: 14 }} onClick={addAccused}>
                + Add Accused/Suspect
              </button>
            </>
          )}
        </div>
      </div>

      <SectionCard title={f.sectionTimeline}>
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.urgencyLevel} <span className="req">*</span>
            </label>
            <WizardSelect
              id="crim-urgency"
              hasError={Boolean(errors.urgencyLevelId)}
              value={draft.urgencyLevelId}
              placeholder={f.selectUrgencyLevel}
              options={urgencyLevels.map((u) => ({ id: u.id, label: u.name }))}
              onChange={(id) => {
                const name = urgencyLevels.find((u) => u.id === id)?.name ?? '';
                setDraft((d) => ({ ...d, urgencyLevelId: id, urgencyLevelName: name }));
                clearErr('urgencyLevelId');
              }}
            />
            <FieldError message={errors.urgencyLevelId} />
          </div>
          <div className="ccr-fg">
            <label>{f.deadlineIfAny}</label>
            <FormattedDateInput
              id="crim-deadline"
              className={inpClass(Boolean(errors.deadline))}
              value={draft.deadline}
              minDate={minDeadlineDate}
              enableYmdViews
              onChange={(next) => {
                setDraft((d) => ({ ...d, deadline: next }));
                clearErr('deadline');
                if (next && !isTodayOrFutureIsoDate(next)) {
                  setErrors((e) => ({ ...e, deadline: validationDateFutureOnly() }));
                }
              }}
            />
            <FieldError message={errors.deadline} />
          </div>
        </div>
        <div className="ccr-fg" style={{ marginBottom: 0 }}>
          <label>
            {f.urgencyJustification} <span className="req">*</span>
          </label>
          <textarea
            id="crim-urgencyJustification"
            className={inpClass(Boolean(errors.urgencyJustification))}
            rows={3}
            value={draft.urgencyJustification}
            onChange={(e) => {
              setDraft((d) => ({ ...d, urgencyJustification: maxLen('urgencyJustification', e.target.value, MAX_TEXT) }));
            }}
            placeholder={f.enterUrgencyJustification}
          />
          <FieldError message={errors.urgencyJustification} />
        </div>
      </SectionCard>

      <CriminalWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={3}
        onBack={() => nav(criminalWizardPath(2, draft.draftRequestId))}
        onNext={() => {
          if (validate()) nav(criminalWizardPath(4, draft.draftRequestId));
        }}
        validateBeforeSave={validate}
      />
    </CriminalWizardShell>
  );
}



